import React, { useState, useEffect, memo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { useModalClose } from '../hooks/useModalClose';
import '../styles/CollaboratorsModal.css';

const CollaboratorsModal = memo(function CollaboratorsModal({ isOpen, onClose, listId }) {
  const navigate = useNavigate();
  const { isClosing, handleClose } = useModalClose(onClose);
  const [loading, setLoading] = useState(true);
  const [owner, setOwner] = useState(null);
  const [collaborators, setCollaborators] = useState([]);

  // Handle escape key
  useEscapeKey(isOpen, handleClose);

  useEffect(() => {
    const fetchCollaborators = async () => {
      if (!isOpen || !listId) return;

      setLoading(true);
      try {
        const response = await api.getCollaborators(listId);
        if (response.data) {
          setOwner(response.data.owner);
          setCollaborators(response.data.collaborators || []);
        }
      } catch (err) {
        console.error('Failed to fetch collaborators:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCollaborators();
  }, [isOpen, listId]);

  const handleUserClick = (user) => {
    handleClose();
    // Prefer username for cleaner URLs, fall back to userId
    navigate(`/user/${user.username || user.userId}`);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="collaborators-modal-overlay" onClick={handleClose}>
      <div className={`collaborators-modal${isClosing ? ' closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="collaborators-modal-header">
          <h2>List Contributors</h2>
          <button className="collaborators-modal-close" onClick={handleClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="collaborators-modal-content">
          {loading ? (
            <div className="collaborators-loading">
              <div className="loading-spinner" />
            </div>
          ) : (
            <>
              {/* Owner */}
              {owner && (
                <div className="collaborators-section">
                  <h3 className="collaborators-section-title">Owner</h3>
                  <div
                    className="collaborator-card"
                    onClick={() => handleUserClick(owner)}
                  >
                    <div className={`collaborator-card-avatar ${owner.isPremium ? 'premium-avatar' : ''}`}>
                      {(owner.profilePicture || owner.photoURL) ? (
                        <img src={owner.profilePicture || owner.photoURL} alt="" className="collaborator-card-photo" />
                      ) : (
                        <span>{(owner.displayName || owner.username || 'U').charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="collaborator-card-info">
                      <span className="collaborator-card-name">{owner.displayName || owner.username}</span>
                      {owner.username && (
                        <span className="collaborator-card-username">@{owner.username}</span>
                      )}
                    </div>
                    <svg className="collaborator-card-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </div>
                </div>
              )}

              {/* Collaborators */}
              {collaborators.length > 0 && (
                <div className="collaborators-section">
                  <h3 className="collaborators-section-title">
                    Collaborators ({collaborators.length})
                  </h3>
                  {collaborators.map((collab) => (
                    <div
                      key={collab.userId}
                      className="collaborator-card"
                      onClick={() => handleUserClick(collab)}
                    >
                      <div className={`collaborator-card-avatar ${collab.isPremium ? 'premium-avatar' : ''}`}>
                        {(collab.profilePicture || collab.photoURL) ? (
                          <img src={collab.profilePicture || collab.photoURL} alt="" className="collaborator-card-photo" />
                        ) : (
                          <span>{(collab.displayName || collab.username || 'U').charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="collaborator-card-info">
                        <span className="collaborator-card-name">{collab.displayName || collab.username}</span>
                        {collab.username && (
                          <span className="collaborator-card-username">@{collab.username}</span>
                        )}
                      </div>
                      <svg className="collaborator-card-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
});

export default CollaboratorsModal;
